import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { sessionQuery, sessionQueryKey } from '@/entities/session';
import { ForgotPasswordForm, ResetPasswordForm } from '@/features/auth';
import { verifyEmail } from '@/shared/api/auth';
import type { User } from '@/shared/api/types';
import { ErrorMessage } from '@/shared/ui';

/** Asks for a password reset link. */
export function ForgotPasswordPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section>
      <h1>{t('auth.forgotTitle')}</h1>
      <ForgotPasswordForm />
      <p>
        <Link to="/login">{t('auth.toLogin')}</Link>
      </p>
    </section>
  );
}

/** Sets a new password from the link in the reset email. */
export function ResetPasswordPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);

  return (
    <section>
      <h1>{t('auth.resetTitle')}</h1>
      {done ? (
        <p role="status">
          {t('auth.resetDone')} <Link to="/login">{t('auth.submitLogin')}</Link>
        </p>
      ) : (
        <ResetPasswordForm
          token={params.get('token') ?? ''}
          onDone={() => {
            setDone(true);
          }}
        />
      )}
    </section>
  );
}

/**
 * Confirms the address as soon as the page opens. A ref guards the request:
 * React runs effects twice in strict mode and the mutation is still idle on
 * the second run, so without it the one-time token would be spent twice and
 * the second answer would report a failure for a link that worked. A request
 * for the session may have left before the address was confirmed; it is
 * cancelled, so that its answer cannot overwrite the confirmed user.
 */
export function VerifyEmailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const token = params.get('token') ?? '';

  const mutation = useMutation({
    mutationFn: () => verifyEmail(token),
    onSuccess: async (user: User) => {
      await queryClient.cancelQueries({ queryKey: sessionQueryKey });
      const current = queryClient.getQueryData<User | null>(sessionQueryKey);

      if (current === undefined) {
        await queryClient.query({ ...sessionQuery, staleTime: 0 });
      } else if (current?.id === user.id) {
        queryClient.setQueryData<User | null>(sessionQueryKey, user);
      }
    },
  });

  const sent = useRef(false);
  const { mutate } = mutation;
  useEffect(() => {
    if (!sent.current) {
      sent.current = true;
      mutate();
    }
  }, [mutate]);

  return (
    <section>
      <h1>{t('auth.verifyTitle')}</h1>
      {mutation.isPending && <p>{t('auth.verifyPending')}</p>}
      {mutation.isSuccess && (
        <p role="status">
          {t('auth.verifyDone')} <Link to="/decks">{t('menu.decks')}</Link>
        </p>
      )}
      {mutation.isError && (
        <>
          <p>{t('auth.verifyFailed')}</p>
          <ErrorMessage error={mutation.error} />
        </>
      )}
    </section>
  );
}
